/**
 * templateVariables.js
 *
 * Defines the available template variable categories + fields,
 * and the BlockNote inline-content spec for rendering them as chips.
 */
import { createReactInlineContentSpec } from "@blocknote/react";

// ─── Template Variable Definitions ──────────────────────────────────
// Each category has a label, an icon (optional), and a list of fields.
// Fields have a `key` (the variable name) and a `label` (human-readable).
export const TEMPLATE_VARIABLE_CATEGORIES = [
  {
    category: "profile",
    label: "Profile",
    icon: "👤",
    fields: [
      { key: "firstName", label: "First Name" },
      { key: "lastName", label: "Last Name" },
      { key: "fullName", label: "Full Name" },
      { key: "emailAddress", label: "Email Address" },
      { key: "phoneNumber", label: "Phone Number" },
    ],
  },
  {
    category: "loan",
    label: "Loan",
    icon: "🏦",
    fields: [
      { key: "loanNumber", label: "Loan Number" },
      { key: "loanName", label: "Loan Name" },
      { key: "loanProductType", label: "Loan Product Type" },
      { key: "loanPurpose", label: "Loan Purpose" },
      { key: "loanAmount", label: "Loan Amount" },
      { key: "loanStatus", label: "Loan Status" },
      { key: "interestRate", label: "Interest Rate" },
      { key: "closingDate", label: "Closing Date" },
      { key: "propertyAddress", label: "Property Address" },
    ],
  },
  {
    category: "loanOfficer",
    label: "Loan Officer",
    icon: "🧑‍💼",
    fields: [
      { key: "fullName", label: "Full Name" },
      { key: "emailAddress", label: "Email Address" },
      { key: "phone", label: "Phone" },
      { key: "nmlsId", label: "NMLS ID" },
    ],
  },
  {
    category: "extra",
    label: "Extra",
    icon: "🔗",
    fields: [
      { key: "uniqueLoanAppLink", label: "Unique Loan App Link" },
      { key: "uniqueCreditAuthLink", label: "Unique Credit Auth Link" },
      { key: "borrowerPortalLink", label: "Borrower Portal Link" },
    ],
  },
];

// ─── Flattened list for quick lookups / search ──────────────────────
// Each item: { category, categoryLabel, key, label, variable }
// `variable` is the final tag string, e.g. "loan.loanPurpose"
export const ALL_TEMPLATE_VARIABLES = TEMPLATE_VARIABLE_CATEGORIES.flatMap(
  (cat) =>
    cat.fields.map((field) => ({
      category: cat.category,
      categoryLabel: cat.label,
      categoryIcon: cat.icon,
      key: field.key,
      label: field.label,
      variable: `${cat.category}.${field.key}`,
    })),
);

// ─── BlockNote Inline Content Spec ──────────────────────────────────
// Renders as a styled chip inside the editor, and exports to
// {{category.field}} in the email HTML output.
export const TemplateVariable = createReactInlineContentSpec(
  {
    type: "templateVariable",
    propSchema: {
      variable: { default: "" }, // e.g. "loan.loanPurpose"
      label: { default: "" }, // e.g. "Loan Purpose"
    },
    content: "none",
  },
  {
    render: (props) => {
      const { variable, label } = props.inlineContent.props;
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "3px",
            backgroundColor: "#EDE7F6",
            color: "#5E35B1",
            borderRadius: "4px",
            padding: "1px 6px",
            fontSize: "inherit",
            fontWeight: 600,
            fontFamily: "'SF Mono', 'Fira Code', monospace",
            cursor: "default",
            whiteSpace: "nowrap",
            border: "1px solid #D1C4E9",
            lineHeight: 1.6,
            verticalAlign: "baseline",
          }}
          data-template-variable={variable}
          contentEditable={false}
        >
          <span
            style={{
              fontSize: "0.85em",
              opacity: 0.7,
            }}
          >
            {"{{"}
          </span>
          {label || variable}
          <span
            style={{
              fontSize: "0.85em",
              opacity: 0.7,
            }}
          >
            {"}}"}
          </span>
        </span>
      );
    },
    // When exporting to HTML (for the actual email), output the raw tag
    toExternalHTML: (props) => {
      const { variable } = props.inlineContent.props;
      return <span>{`{{${variable}}}`}</span>;
    },
    // Parse {{variable}} back from HTML into the inline content
    parse: (element) => {
      const attr = element.getAttribute("data-template-variable");
      if (attr) {
        const match = ALL_TEMPLATE_VARIABLES.find((v) => v.variable === attr);
        return {
          variable: attr,
          label: match?.label || attr,
        };
      }
      // Also parse from raw text like {{loan.loanPurpose}}
      const text = element.textContent?.trim();
      if (text?.startsWith("{{") && text?.endsWith("}}")) {
        const variable = text.slice(2, -2).trim();
        const match = ALL_TEMPLATE_VARIABLES.find(
          (v) => v.variable === variable,
        );
        if (match) {
          return { variable, label: match.label };
        }
      }
      return undefined;
    },
  },
);
